import { escapeHtml, styleString } from './email-template-builder.utils';

/** Email uses presentation tables and CID images, never grid or icon fonts. */
export function renderListEmailWidget(data: any, helpers: Set<string>, iconPaths: Record<string, string> = {}): string {
  const bounded = (value: number, fallback: number, min: number, max: number) =>
    Number.isFinite(value) ? Math.max(min, Math.min(max, Math.round(value))) : fallback;
  const items = Array.isArray(data?.items) ? data.items : [];
  const cards = data?.layout === 'cards';
  const table = data?.layout === 'table';
  const columns = cards ? bounded(data?.cardsPerRow, 3, 1, 6) : 1;
  const gap = bounded(data?.cardsGap, 20, 0, 64);
  const headingSize = bounded(data?.headingAppearance?.size, 16, 8, 72);
  const descriptionSize = bounded(data?.descriptionAppearance?.size, 16, 8, 72);
  const iconSize = bounded(data?.iconAppearance?.size, 20, 12, 64);
  const headingColor = data?.headingAppearance?.color || '#091d45';
  const descriptionColor = data?.descriptionAppearance?.color || '#74788d';
  const iconColor = data?.iconAppearance?.color || '#289fe7';
  const rows: string[] = [];
  for (let start = 0; start < items.length; start += columns) {
    const cells: string[] = [];
    for (let col = 0; col < columns; col++) {
      const item = items[start + col];
      const outer = styleString({
        width: `${100 / columns}%`,
        'padding-right': cards && col < columns - 1 ? `${gap}px` : undefined,
        'padding-bottom': cards ? `${gap}px` : undefined,
      });
      if (!item) {
        cells.push(`<td class="tb-list-card" style="${escapeHtml(outer)}"></td>`);
        continue;
      }
      let marker = '';
      if (data?.marker === 'icon' || !data?.marker) {
        const path =
          iconPaths[item.icon || 'check'] || iconPaths.check || 'M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z';
        if (path) {
          helpers.add('socialIcon');
          marker = `<img src="{{socialIcon ${JSON.stringify(path)} ${JSON.stringify(iconColor)} ${iconSize}}}" width="${iconSize}" height="${iconSize}" alt="" style="display:block;border:0;">`;
        }
      } else if (data.marker === 'bullet') marker = '&#8226;';
      else if (data.marker === 'number') marker = `${start + col + 1}.`;
      const markerStyle = `color:${iconColor};font-size:${iconSize}px;line-height:1.2;vertical-align:top;`;
      const title = `<div style="${escapeHtml(`color:${headingColor};font-size:${headingSize}px;font-weight:600;line-height:1.5;`)}">${escapeHtml(item.title)}</div>`;
      const description = item.description?.trim()
        ? `<div style="${escapeHtml(`color:${descriptionColor};font-size:${descriptionSize}px;line-height:1.6;margin-top:4px;`)}">${escapeHtml(item.description).replace(/\r?\n/g, '<br>')}</div>`
        : '';
      const text = title + description;
      let content = cards
        ? `${marker ? `<div style="${escapeHtml(markerStyle + 'margin-bottom:16px;')}">${marker}</div>` : ''}${text}`
        : `<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>${marker ? `<td width="${iconSize + 16}" style="${escapeHtml(markerStyle + 'padding-right:16px;')}">${marker}</td>` : ''}<td valign="top">${text}</td></tr></table>`;
      const href = listHref(item);
      if (href)
        content = `<a href="${escapeHtml(href)}" target="${item.actionPayload?.target === '_self' ? '_self' : '_blank'}" style="display:block;text-decoration:none;">${content}</a>`;
      const inner = styleString({
        padding: cards ? '24px' : '12px',
        'background-color': cards ? data.cardsBackground || 'rgba(127,127,127,0.06)' : undefined,
        border: cards || table ? '1px solid #dddddd' : undefined,
        'border-radius': cards ? '16px' : undefined,
      });
      cells.push(
        `<td${cards ? ' class="tb-list-card"' : ''} valign="top" style="${escapeHtml(outer)}"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td style="${escapeHtml(inner)}">${content}</td></tr></table></td>`,
      );
    }
    rows.push(`<tr>${cells.join('')}</tr>`);
  }
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="width:100%;border-collapse:collapse;table-layout:fixed;">${rows.join('')}</table>`;
}

function listHref(item: any): string {
  const payload = item.actionPayload ?? {};
  if (item.actionType === 'mailTo') {
    const to = String(payload.to ?? '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) return '';
    return `mailto:${to}?subject=${encodeURIComponent(payload.subject || '')}&body=${encodeURIComponent(payload.body || '')}`;
  }
  if (item.actionType === 'phone') {
    const number = String(payload.number ?? '').replace(/[ ()-]/g, '');
    return /^\+?\d{10,15}$/.test(number) ? `tel:${number}` : '';
  }
  if (item.actionType !== 'link' || payload.navigateTo === 'portal') return '';
  const value = String(payload.href ?? '').trim();
  if (!value || (/^[a-z][a-z\d+.-]*:/i.test(value) && !/^https?:\/\//i.test(value))) return '';
  try {
    const url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value.replace(/^\/\//, '')}`);
    return url.hostname.includes('.') ? url.href : '';
  } catch {
    return '';
  }
}
